import { graphql, Script } from "gatsby"
import { Box, Container, Grid, LogoBar } from "@fastly/consistently"
import styled from "styled-components"

import "@fragments/Page"
import { findById } from "@utils/contentful"
import reducePageContent from "@utils/reducePageContent"
import { reduceMicrocopySet } from "@utils/reduceMicrocopy"

import useMedia, { mdMediaQuery } from "@hooks/use-media"

import OnThisPage from "@components/on-this-page/on-this-page"
import { MikanSpan } from "@components/Mikan"
import RichText from "@components/rich-text/renderer"
import { FastlyImage } from "@components/FastlyImage"
import StyledHero from "@components/StyledHero"
import ProductCardsGroup from "@components/ProductCardsGroup/ProductCardsGroup"
import GridText from "@components/GridText"
import SuggestContent from "@components/ProductSuggestContent/SuggestContent"
import GridChild from "@components/GridChild"
import DetailMenu from "@components/DetailMenu"
import Cta from "@components/Cta/Cta"
import BigCard from "@components/BigCard/BigCard"
import { ResponsiveIframe } from "@components/ResponsiveIframe"

import { StyledCenterText } from "@templates/pages/products/styled-products"
import NewsletterForm from "@components/Form/NewsletterForm"

const FastForwardHero = styled(StyledHero)`
  b {
    color: var(--red-light);
  }

  header {
    > div:nth-of-type(1) {
      a:nth-of-type(1) {
        margin-right: var(--size-space-lg);
      }
      a:nth-of-type(2n) {
        margin-left: 0px;
      }
    }

    > div:nth-of-type(2n) {
      grid-row: 1;
    }
  }

  @media (min-width: 768px) {
    header > div:nth-of-type(2n) {
      grid-row: inherit;
    }
  }
`

const DarkContainer = styled(Container)`
  color: var(--grey-70);

  h2 {
    color: var(--color-header-text);
  }
`

const CustomerCommunity = styled.div`
  > p:first-child {
    font-weight: 700;
    line-height: 125%;
    text-transform: uppercase;
    letter-spacing: 0.0625rem;
  }
`

const FastForward = ({ data: { page } }) => {
  const pageContent = reducePageContent(page)
  const microcopySet = pageContent["3YDwY8y2hKshC84bHLKBgE"]
  const labels = reduceMicrocopySet(microcopySet)

  const hero = pageContent["2UhG7WHFNTmNT4JmRaGojM"]
  const heroText = findById(hero, "6VOxlFSI7a5PiIqlK3qZkw")
  const heroImage = findById(hero, "Yovx02thBpqwcsqIhWiAf").content?.file?.url

  const fastForwardLogo = pageContent["151ZLrPr9jB67nGNmo1KGE"].content?.file?.url

  const cardGroup = pageContent["5iCELqYFLSnht7ifXBnIx0"]
  const cardGroupText = findById(cardGroup, "5KTEIT0B3SelkPChhXzxzU")
  const cardGroupItems = findById(cardGroup, "3JfBCfTjycFLWOycyqoXT8").section_content

  const catchAllBlock = pageContent["19XXWm4OBp3zBnh9bUUhW8"]
  const catchAllBlockIntro = findById(catchAllBlock, "1ROmI99qPqUMoAf8yTTAgo")
  const catchAllBlockItems = findById(catchAllBlock, "23tHt8DLypmCTqrcMZrKLq")

  const subHero = pageContent["66o1hLIO97v2nQUFNfZG0j"]
  const subHeroText = findById(subHero, "1MGRZHbJqg756pp9avT2nS")
  const subHeroImage = findById(subHero, "1v1vxk6372An2WRVdAf0p3").content?.file?.url

  const suggestedContent = pageContent["1ikAECNB3MpI2EigPGI1xU"]

  const companies = pageContent["45xrhNbH0IrYWFb2Ty6TKS"]
  const companiesTitle = findById(companies, "6cojwDycvhSQqiFpdAVcC3")
  const companiesImg = findById(companies, "5Euwp7U7GjJm4Sf19vZcn9").section_content

  const policy = pageContent["1VOJJPdHLRqi9vqFBOb5f5"]
  const policyText = findById(policy, "72ODFYwN2sKCDNigc1ocOy")
  const policyImage = findById(policy, "4kBNBCG7DYN27DqTSIiIwk").content?.file?.url

  const fastlyLabs = pageContent["7aXNHzQrMX7O4vu7OWvSeO"]

  const standardsAndProtocols = pageContent["1hVTElAni0sFrZlMW75Njn"].map((item) => (
    <RichText key={item.contentful_id} body={item.rtContent} mikan={item.mikan} />
  ))

  // our projects card group
  const ourProjects = pageContent["1DwG3jQ13pVdv4CZO1mptD"]
  const ourProjectsText = findById(ourProjects, "6flV5B7aSYC3ZsqBqhklUB")
  const ourProjectsItems = findById(ourProjects, "8XV0JYaiXpdYmXC8c48fH").section_content

  const contactArea = pageContent["5zZP96Fqh8SNcCkfc4J2Fr"]
  const contactAreaText = findById(contactArea, "3gg0u9EoIiUxaIBW9p4F6Z")

  const cta = pageContent["7HpLJKGqOxfvgVUmfFIruW"]

  const isMdViewport = useMedia(mdMediaQuery)

  const iframeDimensions = isMdViewport ? { width: 500, height: 575 } : { width: 400, height: 600 }

  const secondaryNavItems = [
    {
      title: labels["fast-forward-program"],
      url: "#fast-forward-program",
      items: null,
    },
    {
      title: labels["customer-community-policy"],
      url: "#customer-community-policy",
      items: null,
    },
    {
      title: labels["fastly-labs"],
      url: "#fastly-labs",
      items: null,
    },
    {
      title: labels["standards-and-protocols"],
      url: "#standards-and-protocols",
      items: null,
    },
    {
      title: labels["our-projects"],
      url: "#our-projects",
      items: null,
    },
    {
      title: labels["apply-now"],
      url: "#apply-now",
      items: null,
    },
  ]

  return (
    <>
      <main>
        {/* Hero */}
        <FastForwardHero theme="dark" layout="aside" addStyles={{ eyebrowType: "tag", tagColor: "var(--red-light)" }}>
          <div>
            <RichText body={heroText.rtContent} mikan={heroText.mikan} />
          </div>
          <Box display="flex" flexDirection="column" justifyContent="center">
            <FastlyImage src={heroImage} dpr={true} params={{ auto: "webp" }} loading="eager" alt="Fast Forward" />
          </Box>
        </FastForwardHero>

        {/* secondary nav */}
        <OnThisPage items={secondaryNavItems} hideIntro secondaryNav />

        {/* Value props */}
        <Container pt="2xl" pb="4xl">
          <Box display="flex" justifyContent="center" pb="2xl">
            <FastlyImage src={fastForwardLogo} loading="eager" />
          </Box>
          <ProductCardsGroup
            type="quadCard"
            text={cardGroupText}
            propsValues={cardGroupItems}
            iconsProps={{ color: "red", width: "97px", height: "52px" }}
            borderColor="var(--color-blue-50)"
            cardColor="var(--color-blue-10)"
          />
        </Container>

        {/* How we grow the open internet grey box */}
        <Container py="4xl">
          <GridText intro={catchAllBlockIntro} items={catchAllBlockItems} />
        </Container>

        {/* sub hero */}
        <StyledHero
          layout="aside"
          id="fast-forward-program"
          addStyles={{ eyebrowType: "tag", tagColor: "var(--blue)", tagBackground: "var(--white)" }}
        >
          <div>
            <RichText body={subHeroText.rtContent} mikan={subHeroText.mikan} />
          </div>
          <Box display="flex" flexDirection="column" justifyContent="center">
            <FastlyImage src={subHeroImage}></FastlyImage>
          </Box>
        </StyledHero>

        {/* Suggested content and log bar */}
        <Container background="var(--grey-lighter)" pb="4xl">
          <SuggestContent
            spacing="0"
            label={labels["read-about-fast-forward-and-program-members"]}
            content={suggestedContent}
            iconsProps={{ color: "blue-dark", width: "26px", height: "26px" }}
          />
        </Container>

        <Container py="4xl">
          <LogoBar logoSize="lg" title={<RichText body={companiesTitle.rtContent} mikan={companiesTitle.mikan} />}>
            {companiesImg.map((l) => (
              <FastlyImage
                src={l.content.file.url}
                dpr={true}
                params={{ height: 48, auto: "avif" }}
                alt=""
                key={l.contentful_id}
              />
            ))}
          </LogoBar>
        </Container>

        {/* Community and Fastly labs */}
        <Container py="5xl">
          <Grid md={2}>
            <CustomerCommunity id="customer-community-policy">
              <RichText body={policyText.rtContent} mikan={policyText.mikan} />
            </CustomerCommunity>
            <FastlyImage src={policyImage} />
          </Grid>
          <Box pt="8xl" id="fastly-labs">
            <StyledCenterText>
              <RichText body={fastlyLabs.rtContent} mikan={fastlyLabs.mikan} />
            </StyledCenterText>
          </Box>
        </Container>

        {/* products line of business*/}
        <Container background="var(--grey-20)" py="6xl" id="standards-and-protocols">
          <Grid sm={1} lg={8}>
            <Box gridColumn={{ lg: "1/4" }}>
              <h2>
                <MikanSpan>{labels["standards-and-protocols"]}</MikanSpan>
              </h2>
            </Box>
            <GridChild lg={"4/-1"}>
              <DetailMenu items={standardsAndProtocols} />
            </GridChild>
          </Grid>
        </Container>

        {/* Our projects */}
        <Container pt="4xl" pb="6xl" id="our-projects">
          <ProductCardsGroup
            type="quadCard"
            text={ourProjectsText}
            propsValues={ourProjectsItems}
            iconsProps={{ color: "red", width: "97px", height: "52px" }}
          />
        </Container>

        {/* Contact area */}
        <DarkContainer py="8xl" background="var(--grey-10)" id="apply-now">
          <Grid lg={2}>
            <Box>
              <RichText body={contactAreaText.rtContent} mikan={contactAreaText.mikan} />
            </Box>

            <Box backgroundColor="white" borderRadius="lg" p="sm">
              <ResponsiveIframe
                id="ff-form"
                title="Fast Forward Application Form"
                src="https://docs.google.com/forms/d/e/1FAIpQLSd4HeH_zJZd6vNyX1wvd05vqEcTXz_llytfthgqYCnGOzYCPw/viewform?embedded=true"
                {...iframeDimensions}
                marginHeight="0"
                marginWidth="0"
              />
            </Box>
          </Grid>
        </DarkContainer>

        {/* CTA */}
        <Container background="var(--color-grey-20)" py="8xl" id="newsletter">
          <BigCard theme="blueDark">
            <Cta type="prefooter">
              <RichText body={cta.rtContent} key={cta.contentful_id} mikan={cta.mikan} />
              <NewsletterForm />
            </Cta>
          </BigCard>
        </Container>
      </main>
      <Script async src="/fst-post-back" />
    </>
  )
}

export default FastForward

export const query = graphql`
  query ($contentful_id: String!) {
    page: contentfulPage(contentful_id: { eq: $contentful_id }) {
      ...Page
    }
  }
`

export { Head } from "@components/Head/Head"
